import { alpha, Box, styled, SxProps, Theme, useTheme } from '@mui/material';
import classnames from 'classnames';
import { memo } from 'react';

import {
  GlowField,
  useCaseBorderHighlight,
  useOverflowMask,
  useRegisterCaseCardAnchor,
} from '#shared';

import { cardWidthMap, caseStatusMapShort } from '../../Case';
import { ENOPCaseStatus } from '../../enums';
import { TrialBadge } from '../../TrialProgram';

import { NewCaseCardContentCompact } from './NewCaseCardContentCompact';
import { NewCaseCardContentFull } from './NewCaseCardContentFull';

import type { NewCase } from '../../types';

interface NewCaseCardProps {
  data: NewCase & {
    updatedFields?: string[];
    isNewOnBoard?: boolean;
  };
  highlightDuration?: number;
  width?: number;
  sx?: SxProps<Theme>;
  onClick?: (id: string) => void;
  side?: 'left' | 'right';
}

const PREFIX = 'TSMED-NewCaseCard';

const newCaseCardClasses = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  headerLeft: `${PREFIX}-headerLeft`,
  statusChip: `${PREFIX}-statusChip`,
  donorType: `${PREFIX}-donorType`,
  caseName: `${PREFIX}-caseName`,
  oscStatus: `${PREFIX}-oscStatus`,
  locationProgress: `${PREFIX}-locationProgress`,
  path: `${PREFIX}-path`,
  pathFrom: `${PREFIX}-pathFrom`,
  pathTo: `${PREFIX}-pathTo`,
  timeInfo: `${PREFIX}-timeInfo`,
  donorOrTime: `${PREFIX}-donorOrTime`,
  caseTimeEnd: `${PREFIX}-caseTimeEnd`,
  location: `${PREFIX}-location`,
  trialDonorGroup: `${PREFIX}-trialDonorGroup`,
};

interface RootProps {
  highlightBorder?: boolean;
  isActiveStatus?: boolean;
  cardWidth: number;
  isPendingLogistics?: boolean;
}

const FULL_CARD_MIN_WIDTH = 170;
const Root = styled(Box, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
  shouldForwardProp: prop =>
    !['highlightBorder', 'cardWidth', 'isPendingLogistics'].includes(
      String(prop),
    ),
})<RootProps>(({
  theme: { palette, spacing, utils },
  highlightBorder,
  cardWidth,
  isPendingLogistics,
}) => {
  const isSM = cardWidth <= FULL_CARD_MIN_WIDTH;

  return {
    color: palette.orange[500],
    position: 'relative',
    background: isPendingLogistics
      ? `radial-gradient(ellipse at top right, ${alpha(palette.orange[500], 0.15)} 0%, transparent 65%), ${alpha(palette.mapBlue[900], 0.6)}`
      : alpha(palette.mapBlue[900], 0.6),
    border: `1px solid ${
      isPendingLogistics
        ? alpha(palette.orange[500], highlightBorder ? 0.5 : 0.3)
        : highlightBorder
          ? palette.orange[500]
          : alpha(palette.orange[500], 0.6)
    }`,
    borderRadius: '20px 2px',
    padding: spacing(1.5, 1),
    height: 160,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: spacing(1),

    ['&:hover']: {
      background: isPendingLogistics
        ? `radial-gradient(ellipse at top right, ${alpha(palette.orange[500], 0.45)} 0%, ${alpha(palette.mapBlue[700], 0.65)} 65%)`
        : alpha(palette.mapBlue[700], 0.6),
      borderColor: isPendingLogistics
        ? alpha(palette.orange[500], 0.5)
        : palette.orange[500],
      cursor: 'pointer',
    },

    [`.${newCaseCardClasses.header}`]: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing(0, 1),
      marginBottom: spacing(2),
      flexWrap: isSM ? 'wrap' : 'nowrap',
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      [`.${newCaseCardClasses.caseName}`]: {
        fontSize: utils.clamp(14, 16),
      },
    },

    [`.${newCaseCardClasses.headerLeft}`]: {
      display: 'flex',
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap',
      gap: spacing(0, 1),
      flex: '0 0 auto',
      minWidth: 0,
    },

    [`.${newCaseCardClasses.oscStatus}`]: {
      fontSize: utils.clamp(14, 16),
      ...(isSM
        ? {
            order: 2,
            flexGrow: 0,
            flexShrink: 0,
            flexBasis: '100%',
            width: '100%',
            marginLeft: 0,
            display: 'flex',
            justifyContent: 'flex-start',
          }
        : {
            order: 1,
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: 'auto',
            marginLeft: 'auto',
            display: 'flex',
            justifyContent: 'flex-end',
          }),
      minWidth: 0,
      gap: spacing(0, 0.5),
      alignItems: 'center',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      direction: 'ltr',
    },

    [`.${newCaseCardClasses.statusChip}`]: {
      display: 'inline-flex',
      alignItems: 'center',
      minWidth: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',

      '> span': {
        display: 'inline-flex',
        padding: '4px 6px',
        background: isPendingLogistics
          ? palette.orange[500]
          : palette.background.default,
        color: isPendingLogistics ? palette.mapBlue[900] : 'inherit',
        fontSize: '10px',
        borderRadius: '5px',
        fontWeight: isPendingLogistics ? 700 : undefined,
      },
    },

    [`.${newCaseCardClasses.trialDonorGroup}`]: {
      display: 'flex',
      gap: spacing(1),
      alignItems: 'center',

      [`.${newCaseCardClasses.donorType}`]: {
        fontSize: utils.clamp(14, 16),
        fontFamily: "'JetBrains Mono', monospace",
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
      },
    },

    [`.${newCaseCardClasses.location}`]: {
      display: 'flex',
      flexDirection: 'column',
      [`.${newCaseCardClasses.locationProgress}`]: {
        position: 'relative',
        height: 35,
        [`&:before`]: {
          content: '""',
          position: 'absolute',
          top: 'calc(50% - 2px)',
          left: 0,
          right: 0,
          height: 4,
          backgroundColor: palette.background.default,
        },
        [`&:after`]: {
          content: '""',
          position: 'absolute',
          top: 'calc(50% - 2.5px)',
          right: 0,
          height: 5,
          width: 5,
          borderRadius: '50%',
          border: `1.25px solid ${palette.mapBlue[400]}`,
        },
        svg: {
          position: 'absolute',
          fill: 'currentColor',
          height: 35,
          width: 35,
          padding: spacing(0.5),
        },
      },
      [`.${newCaseCardClasses.path}`]: {
        display: 'flex',
        flexWrap: 'nowrap',
        fontSize: utils.clamp(10, 14),
        justifyContent: 'space-between',
        gap: spacing(1),
        [`.${newCaseCardClasses.pathFrom}`]: {
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        [`.${newCaseCardClasses.pathTo}`]: {},
      },
    },
    [`.${newCaseCardClasses.timeInfo}`]: {
      display: 'flex',

      alignItems: 'end',
      fontSize: utils.clamp(12, 13),
      [`.${newCaseCardClasses.donorOrTime}`]: {
        color: palette.grey[600],
        marginRight: spacing(1),
      },
      [`.${newCaseCardClasses.caseTimeEnd}`]: {
        color: palette.grey[100],
      },
    },
  };
});

/**
 * NewCaseCard component for displaying new case alerts.
 * Follows the project's card design patterns and performance guidelines.
 */
export const NewCaseCard = memo<NewCaseCardProps>(
  ({ data: newCase, highlightDuration, width = 300, sx, onClick, side }) => {
    const { updatedFields = [], id } = newCase;
    const highlightBorder = useCaseBorderHighlight(updatedFields, false);
    const { palette } = useTheme();
    // TODO: move outside of component
    const anchorRef = useRegisterCaseCardAnchor(id, side);

    const sizeSM = width <= cardWidthMap.SM;
    const isCompact = width <= FULL_CARD_MIN_WIDTH;

    const handleClick = () => onClick?.(id);

    const parts = (newCase.donorORTimeString || '').split(' ');
    const hhmm = parts[0] ?? '';
    const tz = parts[1] ?? '';

    const oscText = caseStatusMapShort[newCase.oscStatus];
    const { ref: chipRef, needsFade } = useOverflowMask<HTMLDivElement>([
      oscText,
      width,
    ]);

    const hasTrial = Boolean(newCase.trialProgram);
    const isPendingLogistics =
      newCase.oscStatus === ENOPCaseStatus.PendingLogistics;

    return (
      <Root
        className={classnames(newCaseCardClasses.root)}
        highlightBorder={highlightBorder}
        cardWidth={width}
        isPendingLogistics={isPendingLogistics}
        width={width}
        sx={sx}
        onClick={handleClick}
      >
        {/* Anchor ref for positioning line to the dot */}
        {side && (
          <Box
            ref={anchorRef}
            sx={{
              position: 'absolute',
              ...(side === 'left'
                ? { top: 0, right: 0 } // top-right corner
                : { bottom: 0, left: 0 }), // bottom-left corner
              width: 0,
              height: 0,
              pointerEvents: 'none',
            }}
          />
        )}

        <Box>
          <Box className={newCaseCardClasses.header}>
            {/* LEFT: label (+ donorType unless SM) */}
            <Box className={newCaseCardClasses.headerLeft}>
              <GlowField
                startColor={palette.orange[500]}
                deps={[newCase.label]}
                className={newCaseCardClasses.caseName}
                ms={highlightDuration}
              >
                {newCase.label}
              </GlowField>

              {!sizeSM && (
                <Box className={newCaseCardClasses.trialDonorGroup}>
                  {hasTrial && <TrialBadge />}
                  <GlowField
                    startColor={palette.orange[500]}
                    deps={[newCase.donorType]}
                    className={newCaseCardClasses.donorType}
                    ms={highlightDuration}
                  >
                    {newCase.donorType}
                  </GlowField>
                </Box>
              )}
            </Box>

            {/* RIGHT: (on SM include donorType) + status chip */}
            <Box className={newCaseCardClasses.oscStatus}>
              {sizeSM && (
                <Box className={newCaseCardClasses.trialDonorGroup}>
                  {hasTrial && <TrialBadge />}
                  <GlowField
                    startColor={palette.orange[500]}
                    deps={[newCase.donorType]}
                    className={newCaseCardClasses.donorType}
                    ms={highlightDuration}
                  >
                    {newCase.donorType}
                  </GlowField>
                </Box>
              )}

              <Box
                ref={chipRef}
                className={newCaseCardClasses.statusChip}
                data-fade={needsFade ? 1 : 0}
              >
                <GlowField
                  component="span"
                  startColor={palette.orange[500]}
                  deps={[oscText]}
                  ms={highlightDuration}
                >
                  {oscText}
                </GlowField>
              </Box>
            </Box>
          </Box>

          {isCompact ? (
            <NewCaseCardContentCompact
              data={newCase}
              highlightDuration={highlightDuration}
            />
          ) : (
            <NewCaseCardContentFull
              data={newCase}
              highlightDuration={highlightDuration}
            />
          )}
        </Box>

        {/* FOOTER */}
        <Box className={newCaseCardClasses.timeInfo}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: sizeSM ? 'column' : 'row',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
            }}
          >
            <GlowField
              startColor={palette.grey[600]}
              deps={[newCase.donorORTimeString]}
              ms={highlightDuration}
              className={newCaseCardClasses.donorOrTime}
            >
              {`OR ${hhmm}${tz ? ` ${tz}` : ''}`}
            </GlowField>

            <Box className={newCaseCardClasses.caseTimeEnd}>
              {newCase.caseTimerEndString}
            </Box>
          </Box>
        </Box>
      </Root>
    );
  },
);

NewCaseCard.displayName = 'NewCaseCard';
